import React, { useState, useEffect } from 'react'
import { useSession } from 'src/sdk/session'
import { useWishlistContext } from 'src/contexts/WishlistContext/wishlist-context'
import WishlistHeart from 'src/components/ui/WishlistHeart'
import Spinner from 'src/components/common/Spinner'
import ReleaseTag from 'src/components/common/ReleaseTag'
import StampsShelf from 'src/components/common/StampsShelf'
import { useStampsDataContext } from 'src/contexts/stamps-data-context'
import { useAddToWishlist } from 'src/sdk/analytics/hooks/useAddToWishlistEvent'
import { useRemoveFromWishlist } from 'src/sdk/analytics/hooks/useRemoveFromWishlistEvent'
import type { CurrencyCode } from '@faststore/sdk'
import { getCookie } from 'src/utils/cookie'
import {
  getCategorie,
  getItemSizeFromProperties,
  getVariantWithSmallestSize,
} from 'src/utils/getProductDetailsInfo'

import type { ProductType, Variant } from './ProductCardTypes'
import type { KitOrPDPProps } from './KitOrPDPImage'
import type { DataSimilars } from '../SimilarProducts/SimilarProducts'
import type { SKUPriceRange } from './utils/getSKUPriceRange'
import { handleHover } from './handleHover'
import {
  hasSpecialNightCollection,
  RenderCollectionTags,
} from './tagsCollection'
import KitOrPDPImage from './KitOrPDPImage'
import ProductClusterHighlights from './ProductClusterHighlights'
import HandleMouseOver from './HandleMouseOver'
import HandlePrice from './HandlePrice'
import ProductName from './ProductName'
import { getSimilarProductsCached } from '../SimilarProducts/getSimilarProductsCached'
import isTouchDevice from './utils/isTouchDevice'

import './product-card.scss'

interface ProductCardReturnProps {
  shelfClass: string
  productImages: Array<{
    url: string
    alternateName: string
  }>
  setIsMouseOver: React.Dispatch<React.SetStateAction<boolean>>
  setCardImage: React.Dispatch<React.SetStateAction<string>>
  cardRef: (node?: Element | null) => void
  KitOrPDPObject: KitOrPDPProps
  product: ProductType
  index: number
  wishlistProduct: WishlistProductProps[]
  linkProps: LinkPropsType
  wishlistStyle?: React.CSSProperties
  newVariantsInStock: Variant[]
  collections: CollectionsType
  allCollections: CollectionsType
  collectionId?: CollectionIdProps
  isNewRelease: boolean
  isKit: boolean
  isNotebook: boolean
  newSpotPrice: number
  newListPrice: number
  newPrice: number
  newIsProductAvailable: boolean
  haveVariants: boolean
  variants: Variant[]
  buyDisabled: boolean
  buyProps: BuyPropsTypes
  hasPolarizedLens?: boolean
  isMouseOver: boolean
  name: string
  isMobile: boolean
  setCurrentProduct: React.Dispatch<React.SetStateAction<ProductType>>
  renderVivaraYou: boolean
  border?: string
  inView: boolean
  skuPriceRange: SKUPriceRange | null
  isRelatedProducts?: boolean
}

export interface SimilarityReturn {
  hasSimilars: boolean
  similarity: Array<{
    value: string
  }>
}

const ProductCardReturn = ({
  shelfClass,
  productImages,
  setIsMouseOver,
  setCardImage,
  cardRef,
  KitOrPDPObject,
  product,
  index,
  wishlistProduct,
  linkProps,
  wishlistStyle,
  newVariantsInStock,
  collections,
  allCollections,
  collectionId,
  isNewRelease,
  isKit,
  isNotebook,
  newSpotPrice,
  newListPrice,
  newPrice,
  newIsProductAvailable,
  haveVariants,
  variants,
  buyDisabled,
  buyProps,
  hasPolarizedLens,
  isMouseOver,
  name,
  isMobile,
  renderVivaraYou,
  border,
  inView,
  skuPriceRange,
  isRelatedProducts,
}: ProductCardReturnProps) => {
  const [similars, setSimilars] = useState<DataSimilars | null>(null)
  const [mobileSimilars, setMobileSimilars] = useState<SimilarityReturn>(
    {} as SimilarityReturn
  )

  const [specialNightCollection, setSpecialNightCollection] =
    useState<CollectionsType>()

  const [prevId, setPrevId] = useState<string>('')
  const [, setIsTouch] = useState<boolean>(false)
  const [hasFetchedSimilarData, setHasFetchedSimilarData] =
    useState<boolean>(false)

  const [isChanging, setIsChanging] = useState<boolean>(false)
  const { removeProductFromWishlist, addProductToWishlist } =
    useWishlistContext()

  const { stampsData } = useStampsDataContext()
  const { sendAddToWishlistEvent } = useAddToWishlist()
  const { sendRemoveFromWishlistEvent } = useRemoveFromWishlist()
  const {
    person,
    currency: { code },
  } = useSession()

  const uuidToUse = getCookie('uuid')

  useEffect(() => {
    setIsChanging(false)
  }, [product?.sku])

  useEffect(() => {
    setIsTouch(isTouchDevice())
  }, [])

  useEffect(() => {
    if (!collections) {
      return
    }

    setSpecialNightCollection(hasSpecialNightCollection(collections))
  }, [collections])

  useEffect(() => {
    const productId = product?.isVariantOf?.productGroupID

    if (inView && product?.id !== prevId) {
      getSimilarProductsCached({ setMobileSimilars, productId })
      setPrevId(product?.id)
    }
  }, [inView, product?.id, prevId])

  const productToUse = getVariantWithSmallestSize(product)

  const skuId = productToUse?.sku

  const productIdMatch = product?.slug?.match(/\b[a-z]{2}\d{8}\b/)

  const productIdValue = productIdMatch ? productIdMatch[0]?.toUpperCase() : ""

  const productName = product?.name

  const productPrice = productToUse?.offers?.offers?.[0]?.price

  const productDiscount = ((product?.offers?.offers?.[0]?.listPrice - product?.offers?.offers?.[0]?.price) * 1)

  const productType = product?.name?.match(/^\p{L}+/u)?.[0] ?? ""

  const productWeight = product?.name?.match(/(\d+)\s*k/i)?.[0] ?? ""

  const productBrand = product?.brand?.name

  const productReference = productToUse?.gtin

  const categories = getCategorie(
    product?.breadcrumbList?.itemListElement?.map((category) => category),
    productName
  )

  const productCollection =
    String(
      product?.isVariantOf?.additionalProperty?.find(
        (property) => property?.name?.toLowerCase() === 'coleção'
      )?.value ?? ''
    ) || ''

  const productMaterial =
    String(
      product?.isVariantOf?.additionalProperty?.find(
        (property) => property?.name?.toLowerCase() === 'material'
      )?.value ?? ''
    ) || ''

  const productSize = getItemSizeFromProperties(
    product?.isVariantOf?.additionalProperty,
    productToUse?.additionalProperty
  )

  const productStone =
    String(
      product?.isVariantOf?.additionalProperty?.find(
        (property) => property?.name?.toLowerCase() === 'pedras'.toLowerCase()
      )?.value ?? ''
    ) || ''

  const productAudience =
    String(
      product?.isVariantOf?.additionalProperty?.find(
        (property) => property?.name?.toLowerCase() === 'sugestão'.toLowerCase()
      )?.value ?? ''
    ) || ''

  const hasStoneOrMaterial =
    mobileSimilars?.similarity?.some(
      (similar) => similar?.value === 'material' || similar?.value === 'pedra'
    ) ?? false

  return (
    <div
      className={shelfClass}
      style={{ border: `1px solid ${border}` }}
      onMouseEnter={() =>
        handleHover({
          isMouseIn: true,
          productImages,
          setIsMouseOver,
          setCardImage,
          isMobile,
          setSimilars,
          similars,
          product,
          hasFetchedSimilarData,
          setHasFetchedSimilarData,
        })
      }
      onMouseLeave={() =>
        handleHover({
          isMouseIn: false,
          productImages,
          setIsMouseOver,
          setCardImage,
          isMobile,
        })
      }
      ref={cardRef}
    >
      {isChanging && (
        <div className="shelf-changing">
          <Spinner customClass="shelf-changing-spinner" />
        </div>
      )}
      {newVariantsInStock?.length > 0 && specialNightCollection && (
        <ReleaseTag
          variant="product-card-special-night"
          name={`${specialNightCollection[0]?.name}`}
        />
      )}
      {newVariantsInStock?.length > 0 && (
        <div className="shelf-product-card__stamp-wrapper-top">
          <StampsShelf
            stampVerticalPosition="1"
            allItems={stampsData}
            collections={allCollections}
          />
        </div>
      )}
      <KitOrPDPImage
        {...KitOrPDPObject}
        product={product}
      />
      {isMouseOver && (
        <WishlistHeart
          products={[wishlistProduct?.[0]?.product]}
          hasBackgroundColor={false}
          onFilledHeartClick={() => {
            removeProductFromWishlist(
              [{ ...wishlistProduct?.[0]?.product }],
              index
            )

            sendRemoveFromWishlistEvent({
              sku_id: skuId,
              item_id: productIdValue,
              item_name: productName,
              item_price: productPrice,
              item_type: productType,
              item_weight: productWeight,
              item_brand: productBrand,
              item_collection: productCollection,
              item_reference: productReference,
              item_material: productMaterial,
              item_category: categories[0] ?? "",
              item_category2: categories[1] ?? "",
              item_category3: categories[2] ?? "",
              item_category4: categories[3] ?? "",
              item_category5: categories[4] ?? "",
              item_category6: categories[5] ?? "",
              item_currency: 'BRL',
              item_size: productSize ?? "",
              item_stone: productStone,
              item_audience: productAudience,
              quantity: 1,
              discount: productDiscount,
              currency: code as CurrencyCode,
              value: Number(productPrice?.toFixed(2)),
              user_logged: !!person?.id,
              user_id: person?.id ?? null,
              uuid: uuidToUse,
            })
          }}
          onEmptyHeartClick={() => {
            addProductToWishlist(wishlistProduct)
            sendAddToWishlistEvent({
              sku_id: skuId,
              item_id: productIdValue,
              item_name: productName,
              item_price: productPrice,
              item_type: productType,
              item_weight: productWeight,
              item_brand: productBrand,
              item_collection: productCollection,
              item_reference: productReference,
              item_material: productMaterial,
              item_category: categories[0] ?? "",
              item_category2: categories[1] ?? "",
              item_category3: categories[2] ?? "",
              item_category4: categories[3] ?? "",
              item_category5: categories[4] ?? "",
              item_category6: categories[5] ?? "",
              item_currency: 'BRL',
              item_size: productSize ?? "",
              item_stone: productStone,
              item_audience: productAudience,
              quantity: 1,
              discount: productDiscount,
              currency: code as CurrencyCode,
              value: Number(productPrice?.toFixed(2)),
              user_logged: !!person?.id,
              user_id: person?.id ?? null,
              uuid: uuidToUse,
            })
          }}
          pdpPath={linkProps.to}
          wishlistStyle={wishlistStyle}
        />
      )}
      <div className={`${shelfClass}__content`}>
        {!newVariantsInStock?.length && (
          <div className={`${shelfClass}__unavailable-flag`}>Indisponível</div>
        )}
        {newVariantsInStock?.length > 0 && (
          <RenderCollectionTags
            collections={collections}
            collectionId={collectionId}
            isNewRelease={isNewRelease}
          />
        )}
        {newVariantsInStock?.length > 0 && (
          <div className="shelf-product-card__stamp-wrapper-middle">
            <StampsShelf
              stampVerticalPosition="2"
              allItems={stampsData}
              collections={allCollections}
            />
          </div>
        )}
        <div className={`${shelfClass}__content-similars`}>
          <p
            className="product-cardsimilar-title"
            style={{
              color: '#737373',
              fontWeight: '700',
              fontSize: '10px',
              fontFamily: 'Roboto, sans-serif',
              lineHeight: '10px',
              letterSpacing: '0.1em',
              textAlign: 'center',
            }}
          >
            {mobileSimilars?.hasSimilars &&
              `${hasStoneOrMaterial ? '+ OPÇÕES DE CORES' : '+ OPÇÕES'}
                `}
          </p>
        </div>

        <ProductName
          linkProps={linkProps}
          name={name}
          shelfClass={shelfClass}
          isKit={isKit}
          product={product}
          isRelatedProducts={isRelatedProducts}
        />
        <div className={`${shelfClass}__cost`}>
          {!isMouseOver || !isNotebook ? (
            <>
              <HandlePrice
                spotPrice={newSpotPrice}
                listPrice={newListPrice}
                price={newPrice}
                isProductAvailable={newIsProductAvailable}
                shelfClass={shelfClass}
                isVivaraYou={renderVivaraYou}
                collectionId={collectionId}
                hasPolarizedLens={hasPolarizedLens}
                skuPriceRange={skuPriceRange}
              />
              <ProductClusterHighlights product={product} />
            </>
          ) : (
            <HandleMouseOver
              haveVariants={haveVariants}
              variants={variants}
              buyDisabled={buyDisabled}
              price={newPrice}
              buyProps={buyProps}
              linkProps={linkProps}
              isKit={isKit}
            />
          )}
          {hasPolarizedLens && !isMouseOver && (
            <div className="polarizedLensTag">
              <p>Lentes Polarizadas</p>
            </div>
          )}
        </div>
        {newVariantsInStock?.length > 0 && (
          <div className="shelf-product-card__stamp-wrapper-bottom">
            <StampsShelf
              stampVerticalPosition="3"
              allItems={stampsData}
              collections={allCollections}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductCardReturn
